import React, { useState } from 'react';
import useProfile from '../../Hooks/userProfileHook';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Inline } from '@/Components/ui/layout';
import { Stack } from '@/Components/ui/layout';
import { Heading } from '@/Components/ui/typography';
import { Text } from '@/Components/ui/typography';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/Components/ui/dropdown-menu';
import { Bookmark, MoreVertical, Mail, MessageCircle, Download, FileText, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export default function CandidateCard({ candidate, onSave }) {
  const [showDropdown, setShowDropdown] = useState(false);

  const { profileData} = useProfile(candidate.candidateId);

  const handleSendEmail = () => {
    const email = candidate.email || ""; 
    const subject = "Job Opportunity";
    const body = `Hello ${candidate.name},\n\nWe have a job opportunity that matches your profile. Let's connect!\n\nBest regards,`;
  
    // Construct Gmail URL
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${email}&su=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;
  
    // Open Gmail in a new tab
    window.open(gmailUrl, "_blank");
  };

  const handleDownloadResume = () => {
    const resumeUrl = candidate.resumeUrl; // Ensure candidate has a resumeUrl property
    const link = document.createElement("a");
    link.href = resumeUrl;
    link.download = `${candidate.name}_Resume.pdf`; // Customize the download filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Card className={cn(
      "flex items-center justify-between p-5 bg-white border border-border rounded-[10px] mb-5 transition-shadow hover:shadow-md",
      candidate.isSelected && "border-primary border-2"
    )}>
      <CardContent className="flex items-center justify-between w-full p-0 gap-5 flex-wrap">
        <Inline spacing="md" align="center" className="gap-5">
          <div className="w-[50px] h-[50px] rounded-full bg-muted mr-5 flex-shrink-0"></div>
          <Stack spacing="xs" className="flex flex-col">
            <Heading level="h3" className="text-lg font-semibold m-0">
              {profileData.data.firstName} {profileData.data.lastName}
            </Heading>
            <Text size="sm" className="text-muted-foreground">
              {profileData.data.title}
            </Text>
          </Stack>
        </Inline>
        
        <Text size="sm" className="text-muted-foreground hidden md:block">
          {profileData.data.city}
        </Text>
        
        <Text size="sm" className="text-muted-foreground hidden md:block">
          {profileData.data.age}
        </Text>
        
        <Inline spacing="sm" className="flex items-center gap-2.5">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onSave(candidate)}
            className="h-9 w-9"
          >
            <Bookmark className="w-5 h-5" />
          </Button>
          <Button
            variant="default"
            onClick={() => {}}
            className="py-2.5 px-5 bg-[#0a65cc] hover:bg-[#084a9e] text-white rounded-md transition-colors"
          >
            <span>View Profile</span>
            <ChevronRight className="w-4 h-4 ml-1.5" />
          </Button>
          
          <DropdownMenu open={showDropdown} onOpenChange={setShowDropdown}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 hover:bg-accent rounded-full"
              >
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[200px]">
              <DropdownMenuItem onClick={handleSendEmail} className="gap-2.5 px-5 py-2.5 cursor-pointer">
                <Mail className="w-5 h-5" />
                <span className="text-sm font-medium">Send Email</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2.5 px-5 py-2.5 cursor-pointer">
                <MessageCircle className="w-5 h-5" />
                <span className="text-sm font-medium">Start Chat</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDownloadResume} className="gap-2.5 px-5 py-2.5 cursor-pointer">
                <Download className="w-5 h-5" />
                <span className="text-sm font-medium">Download CV</span>
              </DropdownMenuItem>
              <DropdownMenuItem className="gap-2.5 px-5 py-2.5 cursor-pointer">
                <FileText className="w-5 h-5" />
                <span className="text-sm font-medium">View Result</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Inline>
      </CardContent>
    </Card>
  );
}
